import React, {
    ReactElement, useEffect, useRef,
} from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { AiOutlineArrowDown, AiOutlineClose } from "react-icons/ai";
import { FaRegWindowMinimize } from "react-icons/fa";
import { FiHelpCircle, FiMaximize2 } from "react-icons/fi";
import { IoEllipsisVertical } from "react-icons/io5";
import { MdOutlineMarkChatUnread } from "react-icons/md";
import { ColumnData, ColumnGroup, SelectionState } from "../../../pages/agent/selection-state";
import {
    ChatStatus, DashboardGroupBy, DashboardView, TicketPriority, TicketView,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import EditableControl from "../../editable-text/EditableText";
import Card, { CardClone, FEATURE_DARKER, PRIORITY_ICON_IMAGE } from "../card/Card";
import Styles from "./Column.module.scss";
import usePortal from "../../../contexts/PortalContext";

function PriorityHelp() {
    const popover = (
        <Popover>
            <Popover.Header as="h3">Priority Levels</Popover.Header>
            <Popover.Body>
                <ul className="list-unstyled">
                    <li className="mb-1">
                        {PRIORITY_ICON_IMAGE[TicketPriority.Critical]}
                        {" "}
                        - Critical Priority
                    </li>
                    <li className="mb-1">
                        {PRIORITY_ICON_IMAGE[TicketPriority.High]}
                        {" "}
                        - High Priority
                    </li>
                    <li className="mb-1">
                        {PRIORITY_ICON_IMAGE[TicketPriority.Medium]}
                        {" "}
                        - Medium Priority
                    </li>
                    <li className="mb-1">
                        {PRIORITY_ICON_IMAGE[TicketPriority.Low]}
                        {" "}
                        - Low Priority
                    </li>
                </ul>
            </Popover.Body>
        </Popover>
    );
    return (
        <OverlayTrigger trigger="click" placement="right" overlay={popover}>
            <button type="button" className="btn btn-none p-0 text-light btn-sm">
                <FiHelpCircle />
            </button>
        </OverlayTrigger>
    );
}

export function ColumnHeaders({ groupBy, ticketView } : {groupBy: DashboardGroupBy; ticketView: TicketView}) {
    return (

        <div className="ms-3 px-1 d-flex mb-n2 mt-1">
            <div className="col-4" />
            <div className="col-8 d-flex text-start text-gray-600 ps-2">
                <small className="col-4 text-start">
                    Last Message
                </small>
                <small className="col d-flex justify-content-start ms-n3 me-3 align-items-start">
                    <span className="me-1">
                        Priority
                    </span>

                    <PriorityHelp />
                </small>
                {groupBy !== DashboardGroupBy.User && (
                    <small className="col">
                        Requester
                    </small>
                )}

                {groupBy !== DashboardGroupBy.Assignee && ticketView !== TicketView.MyTickets && (
                    <small className="col">
                        Assignee
                    </small>
                )}
                <small className="col">
                    Source
                </small>
                <small className="col">
                    Modified
                </small>
            </div>
        </div>

    );
}

interface ColumnGroupProps {
    group: ColumnGroup;
    hasGroups: boolean
    selectionState: SelectionState;
    canDrag: boolean;
    view: DashboardView;
    groupBy: DashboardGroupBy;
    searchValue: string;
    ticketView: TicketView;
    hasSlas: boolean
}
function ColumnGroupDisplay({
    group, hasGroups, selectionState, canDrag, view, groupBy, searchValue, ticketView, hasSlas,
}: ColumnGroupProps): ReactElement {
    const [isMinimizedState, setMinimized] = selectionState.registerGroupMinimize(group);
    const isMinimized = isMinimizedState && hasGroups;
    const isCompact = view === DashboardView.Compact;
    const shouldDisplaySmall = isMinimized || isCompact;
    const groupUnreadCount = selectionState.getGroupUnreadCount(group);
    const { featureFlags } = usePortal();

    const isFeatureDark = featureFlags.has(FEATURE_DARKER);
    return (
        <div key={group.fullId} className={`${(isCompact && !isMinimized) ? "" : "overflow-hidden"}  group-container`}>
            {hasGroups && (
                <div
                    className={`${(isCompact && !isMinimized) ? "mt-2 mb-0" : "list-header shadow border-top"} ${
                        isCompact ? "py-0" : "pb-2 pt-0"} px-3 rounded-0 `}
                >
                    <div className="d-flex align-items-stretch justify-content-between">
                        <div className="d-flex mx-0 py-2 align-items-center">
                            {!group.hideImage && (
                                <Avatar
                                    src={group.imageUrl}
                                    className=" me-2"
                                    variant={shouldDisplaySmall ? "sm" : "md"}
                                />
                            )}
                            {shouldDisplaySmall ? (
                                <div className="my-1 align-self-center">
                                    <h6 className="card-title mb-0 d-flex align-items-center">

                                        {group.isCentered
                                            ? <div className="align-self-center">{group.title}</div>
                                            : group.title}
                                        {" "}
                                        {group.subtitle ? (
                                            <span className="ms-1">
                                                (
                                                {group.subtitle}
                                                )
                                            </span>
                                        ) : ""}
                                        {groupUnreadCount > 0 && isMinimized && (
                                            <span className="badge bg-danger ms-2" title={`${groupUnreadCount} unread messages`}>
                                                {groupUnreadCount > 99 ? "99+" : groupUnreadCount}
                                                <MdOutlineMarkChatUnread className="ms-1" />
                                            </span>
                                        )}

                                    </h6>
                                    {isMinimized && (
                                        <small>
                                            {group.items.length}
                                            {" "}
                                            tickets
                                        </small>
                                    )}

                                </div>

                            ) : (
                                <div className="card-body pb-0">
                                    <h5 className="card-title">
                                        {group.title}
                                    </h5>
                                    {group.subtitle ? <p className="card-text">{group.subtitle}</p> : ""}
                                </div>
                            )}

                        </div>
                        <div className="d-flex flex-column align-items-end justify-content-between">
                            <button
                                type="button"
                                className="btn btn-none p-0 m-0 border-0"
                                onClick={() => setMinimized(value => !value)}
                            >
                                <small className="text-gray-500">
                                    {isMinimized ? <FiMaximize2 /> : <FaRegWindowMinimize />}
                                </small>

                            </button>
                            {!isMinimized && (
                                <small className="text-end d-block text-light mb-1">
                                    {" "}
                                    {group.items.length}
                                    {" "}
                                    {group.items.length > 1 ? "tickets" : "ticket"}
                                </small>
                            )}
                        </div>

                    </div>

                </div>
            )}

            {!isMinimized && (
                <>
                    {isCompact && (
                        <ColumnHeaders groupBy={groupBy} ticketView={ticketView} />
                    )}

                    <Droppable
                        key={group.fullId}
                        droppableId={group.fullId}
                        type={group.id}
                        renderClone={provided => (
                            <div
                            // eslint-disable-next-line react/jsx-props-no-spreading
                                {...provided.draggableProps}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...provided.dragHandleProps}
                                ref={provided.innerRef}
                            >
                                <CardClone
                                    selectionState={selectionState}
                                    view={view}
                                    groupBy={groupBy}
                                    ticketView={ticketView}
                                />

                            </div>
                        )}
                    >
                        {dragChild => (
                            <div
                                className="card-body p-0 mt-0 column-items"
                                ref={dragChild.innerRef}
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...dragChild.droppableProps}
                            >
                                {/* ${isDraggingOver ? "" : Styles["column-group"]} */}
                                <div className={`column-group  ${Styles["column-group"]}`}>
                                    <div className="list-group list-group-flush mt-2 ms-2 me-2 pb-2">

                                        {group.items.map((item, index) => (

                                            <Draggable
                                                draggableId={item.id}
                                                key={item.id}
                                                index={index}
                                                isDragDisabled={
                                                    (!item.canCurrentUserEdit
                                                    && item.status !== ChatStatus.ChatRequested
                                                    && item.status !== ChatStatus.Connected)
                                                 || !canDrag
                                                }
                                            >
                                                {(provided /* snapshot */) => (
                                                    <div
                                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                                        {...provided.draggableProps}
                                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                                        {...provided.dragHandleProps}
                                                        ref={provided.innerRef}
                                                    >
                                                        <Card
                                                            value={item}
                                                            view={view}
                                                            groupBy={groupBy}
                                                            className={(view === DashboardView.Compact) && !isFeatureDark
                                                                ? "shadow"
                                                                : ""}
                                                            searchParams={{ search: searchValue }}
                                                            ticketView={ticketView}
                                                            hasSlas={hasSlas}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}

                                        {dragChild.placeholder}

                                    </div>
                                </div>

                            </div>
                        )}
                    </Droppable>
                </>

            )}

        </div>
    );
}

interface ColumnProps {
    column: ColumnData;
    index: number;
    dragGroup: string | undefined;
    onColumnChangeName: (columnId: string, name: string) => void;
    hasGroups: boolean;
    canDrag: boolean;
    selectionState: SelectionState;
    view: DashboardView;
    groupBy: DashboardGroupBy;
    searchValue: string;
    ticketView: TicketView;
    hasSlas: boolean;
    dragParentStateId: string | undefined;
}

export default function Column({
    column, index, dragGroup, onColumnChangeName, hasGroups,
    selectionState, canDrag, view, groupBy, searchValue, ticketView,
    hasSlas, dragParentStateId,
}: ColumnProps): ReactElement {
    const { groups } = column;
    const unreadCount = selectionState.registerColumnPill(index);
    const isCompact = view === DashboardView.Compact;
    const hasChildren = column.items.isNotEmpty;
    const { featureFlags } = usePortal();
    const headerRef = useRef<HTMLDivElement>(null);

    const isSourceColumn = column.id === dragParentStateId;
    const isDark = featureFlags.has(FEATURE_DARKER);

    // eslint-disable-next-line no-param-reassign
    // view = DashboardView.Wall;

    // eslint-disable-next-line no-param-reassign
    // dragParentStateId = "hello";
    // const isSourceColumn = column.text === "Waiting for Help ";// column.id === dragParentStateId;

    const classColumn = (!isCompact && dragParentStateId && !isSourceColumn) ? Styles["column-top"] : "";
    // const classColumn = ""; // ;(!isCompact && dragParentStateId && !isSourceColumn)
    //  ? Styles["column-top"] : Styles[`column-wall`];

    const shouldShowPlaceholder = (dragParentStateId && !isSourceColumn)
        || (dragGroup ? !groups.some(group => group.id === dragGroup) : groups.isEmpty);

    let color = isCompact
        ? "bg-gradient-light text-gray-600 border-bottom text-white rounded mb-0 "
        : "bg-gradient text-white card-header rounded-top mb-0 ";
    if (isDark) {
        color = isCompact
            ? `text-blue-dark border-gray-500 sticky-top pt-3 pe-1 w-50 mt-4 ${Styles["column-title"]}`
            : "bg-blue-dark text-white card-header rounded-top mb-0 ";
    }

    const isDarkCompact = isDark && isCompact;
    return (

        <Draggable
            draggableId={`column-${column.id}`}
            key={`column-${column.id}`}
            index={index}
            isDragDisabled={!canDrag}
        >
            {(dragProvided /* snapshot */) => (

                <div
                    className={`mt-0 ${isCompact ? "mb-5" : "mb-4 col-lg-3"}
                        ${Styles[`column-${view.toLowerCase()}`] ?? ""}
                        ${Styles[`column-${isDark ? "dark" : ""}-${view.toLowerCase()}`] ?? ""}
                        ${isCompact && !hasChildren && !dragGroup ? "d-none" : ""}
                    `}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...dragProvided.draggableProps}
                    ref={dragProvided.innerRef}
                >
                    <div className={`px-2${classColumn}`}>
                        <div
                            className={`${isCompact ? "pb-2" : "shadow bg-white"} rounded`}
                        >
                            {canDrag && (
                                <div
                                    ref={headerRef}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...dragProvided.dragHandleProps}
                                    className={`${"fs-5 py-2 px-3 "
                                    + "d-flex align-items-center justify-content-between"} ${color} ${
                                        (isCompact && groupBy === DashboardGroupBy.NoGroup) ? "mb-1" : ""}`}
                                >

                                    {!isDark && isCompact && <IoEllipsisVertical className="text-light ms-n2 me-2" />}
                                    <span className="me-auto mb-0">
                                        <div className={`nav-link list-border-primary ${isDarkCompact
                                            && "fs-5 text-primary d-flex align-items-center "}`}
                                        >
                                            <EditableControl
                                                value={column.text}
                                                onChange={name => onColumnChangeName(column.id, name as string)}
                                            />
                                            {isDarkCompact && (
                                                <small className="ms-2 flex-grow-0 text-light rounded-pill
                                                            bg-blue-light text-white px-2 py-0"
                                                >
                                                    {column.items.length}
                                                </small>
                                            )}

                                            {isDarkCompact && unreadCount > 0 && (
                                                <div className="d-flex fs-6 btn-sm ms-3 px-2 d-block
                                                p-0 text-danger align-items-center border border-gray-500 rounded bg-danger text-white"
                                                >
                                                    <MdOutlineMarkChatUnread className="pt-1" />
                                                    <button
                                                        onClick={() => selectionState.scrollToFirstUnread(index)}
                                                        type="button"
                                                        className="btn btn-sm btn-none p-0 p-0 ms-1 text-white"
                                                    >
                                                        <small>
                                                            {/* <AiOutlineArrowDown className="me-2" /> */}
                                                            {unreadCount > 99 ? "99+" : unreadCount}

                                                        </small>
                                                    </button>

                                                    <button
                                                        onClick={() => selectionState.markAllRead(index)}
                                                        type="button"
                                                        title="Mark all unread conversations as read."
                                                        className="btn btn-sm btn-none ms-3 text-white rounded-0 border-start border-white ps-2"
                                                    >
                                                        <AiOutlineClose />
                                                    </button>
                                                </div>

                                            )}

                                        </div>

                                    </span>
                                    {!isDarkCompact && (
                                        <small className="ms-2 fs-5 flex-grow-0 text-light">
                                            {column.items.length}
                                        </small>
                                    )}

                                </div>
                            )}

                            {!isDarkCompact && unreadCount > 0 && (
                                <div className=" position-relative">
                                    <div className={`d-flex btn-sm bg-danger ${Styles.unread} `
                                    + `p-0 d-block position-absolute top-0 start-50 translate-middle rounded-pill shadow`}
                                    >
                                        <button
                                            onClick={() => selectionState.scrollToFirstUnread(index)}
                                            type="button"
                                            className="btn btn-sm  btn-none ps-3 pe-2 rounded py-2 rounded-pill text-white"
                                        >
                                            <small>
                                                <AiOutlineArrowDown className="me-2" />
                                                {unreadCount > 99 ? "99+" : unreadCount}
                                                {" "}
                                                unread
                                            </small>
                                        </button>
                                        <button
                                            onClick={() => selectionState.markAllRead(index)}
                                            type="button"
                                            title="Mark all unread conversations as read."
                                            className="btn btn-sm btn-none rounded-pill px-2"
                                        >
                                            <AiOutlineClose className="text-white" />
                                        </button>
                                    </div>

                                </div>
                            )}

                            {shouldShowPlaceholder && (
                                <Droppable
                                    key={column.id}
                                    droppableId={column.id}
                                    type={dragGroup}
                                >
                                    {dragChild => (
                                        <div
                                            className="card-body p-0 mt-0 column-items"
                                            ref={dragChild.innerRef}
                                            // eslint-disable-next-line react/jsx-props-no-spreading
                                            {...dragChild.droppableProps}
                                        >

                                            {dragChild.placeholder}
                                            <div className="text-light p-3 d-flex justify-content-center">
                                                Drag/Drop tickets here.
                                            </div>

                                        </div>
                                    )}
                                </Droppable>

                            )}
                            {(!dragParentStateId || isSourceColumn) && groups
                                .filter(grp => !dragGroup || grp.id === dragGroup)
                                .map(group => (
                                    <ColumnGroupDisplay
                                        key={group.fullId}
                                        group={group}
                                        hasGroups={hasGroups}
                                        selectionState={selectionState}
                                        canDrag={canDrag}
                                        view={view}
                                        groupBy={groupBy}
                                        searchValue={searchValue}
                                        ticketView={ticketView}
                                        hasSlas={hasSlas}
                                    />
                                ))}

                        </div>
                    </div>
                </div>
            )}
        </Draggable>

    );
}
